import { motion } from 'framer-motion';
import {
  ArrowRight,
  Phone,
  CheckCircle2,
  Shield,
  BarChart3,
  Target,
  FileCheck,
  Scale,
  TrendingUp,
  Calculator,
  Landmark
} from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { companyInfo } from '../data';

const services = [
  {
    icon: BarChart3,
    title: 'Financial Projection',
    description: 'Layanan ini sesuai untuk Perusahaan & pebisnis yang membutuhkan proyeksi keuangan untuk investor, penilaian kelayakan investasi, atau persiapan modal usaha.',
    features: [
      'Pro-Forma Keuangan',
      'Proyeksi Keuangan (Arus Kas, Laba Rugi, Neraca)',
      'Analisis Rasio Profitabilitas & Strategi Modal',
      'Financial Investment Analysis & Sensitivity Analysis',
      'Business Valuation (Opsional)'
    ],
    suitable: '🏢 Perusahaan & pebisnis yang membutuhkan proyeksi keuangan'
  },
  {
    icon: Target,
    title: 'Strategic Financial Planning & Analysis',
    description: 'Membantu usaha anda dalam menganalisis, evaluasi dan pembentukan rencana strategic keuangan dari sisi resiko dan pengembangan perusahaan.',
    features: [
      'Financial Risk Assessment & Management',
      'Strategic Financial Planning & Advisory',
      'Financial SOP & KPIs development'
    ],
    suitable: '📈 UMKM & perusahaan besar yang ingin menerapkan strategi keuangan optimal'
  },
  {
    icon: FileCheck,
    title: 'Financial Audit & Evaluation',
    description: 'Membantu usaha anda dalam meneliti kekurangan dan error pada proses pencatatan dan kesehatan perusahaan dari sisi keuangan.',
    features: [
      'Evaluasi Perencanaan Keuangan berdasarkan KPI',
      'Audit & Evaluasi Keuangan Tahunan',
      'Pembenahan/Pembetulan Pencatatan Keuangan',
      'Pengembangan SOP Keuangan'
    ],
    suitable: '🏢 Startup & perusahaan skala menengah-besar yang membutuhkan second opinion'
  },
  {
    icon: Scale,
    title: 'Tax Consultation & Reporting',
    description: 'Membantu usaha anda dalam berkonsultasi untuk mendiskusikan perencanaan dan pelaporan perpajakan.',
    features: [
      'Konsultasi Perpajakan',
      'Perencanaan Pelaporan & Strategi Perpajakan',
      'Perencanaan Pelaporan perpajakan (PPh Pasal 23, 22, 21, 4 Ayat 2, Final, PPN, PPh Badan)',
      'Pelaksanaan pelaporan pajak',
      'Poin perhitungan pajak'
    ],
    suitable: '💼 Bisnis & perusahaan yang membutuhkan panduan perpajakan efisien'
  },
  {
    icon: TrendingUp,
    title: 'Investment Appraisal & Market Risk Analysis',
    description: 'Layanan kami dapat membantu anda dalam mengambil keputusan investasi dengan akuisisi asset atau membuat unit usaha baru.',
    features: [
      'Market/Asset Feasibility Analysis',
      'Market Risk Analysis',
      'Proyeksi Keuangan, Arus Kas, Laba Rugi, Neraca dan Analisis',
      'Analisis Sensitivitas',
      'Rasio Profitabilitas dan Strategi Modal'
    ],
    suitable: '🏦 Bisnis yang melakukan ekspansi, merger & akuisisi, joint venture'
  },
  {
    icon: Calculator,
    title: 'Accounting & Financial Reporting',
    description: 'Membantu anda dalam melaksanakan pencatatan akutansi dan keuangan secara komprehensif dan sesuai standar PSAK.',
    features: [
      'Pencatatan Transaksi sesuai Siklus Akuntansi',
      'Pembuatan Laporan Keuangan'
    ],
    suitable: 'Bisnis yang membutuhkan sistem akuntansi yang rapi dan transparan'
  },
  {
    icon: Landmark,
    title: 'CFO Advisory',
    description: 'Pendampingan strategis untuk mengoptimalkan cashflow, profitabilitas, struktur pajak, dan kesehatan finansial bisnis Anda, perencanaan keuangan usaha untuk berbagai jenjang fase bisnis, budgeting operasional dengan analis professional berbasis data, bukan asumsi.',
    features: [
      'Optimasi Cashflow & Profitabilitas',
      'Perencanaan Keuangan Berbasis Fase Bisnis',
      'Budgeting Operasional Professional',
      'Analisis Data-Driven (Bukan Asumsi)',
      'Strategi Struktur Pajak Optimal',
      'Monitoring Kesehatan Finansial'
    ],
    suitable: '🏢 Perusahaan yang butuh pendampingan CFO level strategis'
  }
];

export function FinanceTaxAccounting() {
  const { ref: heroRef, isInView: heroInView } = useScrollAnimation({ threshold: 0.1 });
  const { ref: servicesRef, isInView: servicesInView } = useScrollAnimation({ threshold: 0.1 });

  return (
    <div className="min-h-screen bg-dark-900">
      {/* Hero Section */}
      <section
        ref={heroRef}
        className="relative pt-32 pb-20 overflow-hidden"
      >
        {/* Video Background - z-0 to stay at back */}
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover object-center z-0"
        >
          <source src="/finance-tax-bg.mp4" type="video/mp4" />
        </video>
        
        {/* Dark Navy Blue Overlay */}
        <div className="absolute inset-0 bg-[#0a1628]/85 z-[1]" />

        <div className="relative max-w-7xl mx-auto section-padding z-[2]">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <div className="flex items-center gap-4 mb-4">
              <span className="text-6xl font-display font-bold text-white/10">03</span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-6">
              Finance, Tax & Accounting
            </h1>

            <p className="text-xl text-white/70 mb-8 leading-relaxed">
              Kami menyediakan layanan <strong className="text-primary-400">keuangan, pajak, dan akuntansi</strong> untuk membantu bisnis Anda dalam <strong className="text-primary-400">perencanaan strategis, proyeksi keuangan, audit, serta kepatuhan perpajakan</strong>.
            </p>

            <motion.a
              href={`tel:${companyInfo.phone}`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white font-semibold rounded-full hover:bg-primary-700 transition-colors shadow-lg shadow-primary-500/30"
            >
              <Phone className="w-5 h-5" />
              Konsultasi Gratis
            </motion.a>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section ref={servicesRef} className="py-20">
        <div className="max-w-7xl mx-auto section-padding">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={servicesInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4">
              Layanan Kami
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              Solusi keuangan komprehensif untuk berbagai kebutuhan bisnis Anda
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                animate={servicesInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className="glass rounded-2xl p-6 hover:border-primary-500/30 transition-all duration-300"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary-600/20 flex items-center justify-center flex-shrink-0">
                    <service.icon className="w-6 h-6 text-primary-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-white mb-2">{service.title}</h3>
                    <p className="text-white/60 text-sm mb-4">{service.description}</p>

                    <div className="space-y-2 mb-4">
                      {service.features.map((feature) => (
                        <div key={feature} className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-primary-400 flex-shrink-0 mt-0.5" />
                          <span className="text-white/70 text-sm">{feature}</span>
                        </div>
                      ))}
                    </div>

                    <div className="pt-4 border-t border-white/10">
                      <p className="text-xs text-primary-400 font-medium mb-1">Cocok untuk:</p>
                      <p className="text-white/80 text-sm">{service.suitable}</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-dark-800/50">
        <div className="max-w-7xl mx-auto section-padding">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass rounded-2xl p-8 md:p-12 text-center"
          >
            <div className="flex items-center justify-center gap-4 mb-6">
              <div className="w-12 h-12 rounded-xl bg-primary-600/20 flex items-center justify-center">
                <Shield className="w-6 h-6 text-primary-400" />
              </div>
            </div>

            <h2 className="text-2xl md:text-3xl font-heading font-bold text-white mb-4">
              Kelola Keuangan Bisnis Anda dengan Profesional
            </h2>

            <p className="text-white/60 mb-8 max-w-2xl mx-auto">
              Dapatkan layanan keuangan, pajak, dan akuntansi yang komprehensif untuk pertumbuhan bisnis yang berkelanjutan.
            </p>

            <motion.a
              href={`tel:${companyInfo.phone}`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center gap-2 px-8 py-4 bg-primary-600 text-white font-semibold rounded-full hover:bg-primary-700 transition-colors shadow-lg shadow-primary-500/30"
            >
              <Phone className="w-5 h-5" />
              Konsultasi Gratis
              <ArrowRight className="w-5 h-5" />
            </motion.a>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
